import { getSizeLabel } from "./getSizeLabel";

const formatKeyLabel = (key) =>
  String(key || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const toArrayCustomizations = (item) => {
  const raw = item?.customizations ?? item?.customization ?? [];
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object") {
    return Object.entries(raw).map(([key, value]) => ({
      key,
      label: formatKeyLabel(key),
      value,
    }));
  }
  return [];
};

const normalizeString = (str) => (str ? String(str).trim().toLowerCase() : "");

const pickFlavorName = (item, customizations) => {
  // prefer top-level flavor object/string
  let flavorName =
    typeof item?.flavor === "string" ? item.flavor : item?.flavor?.name;

  // fallback to customization entry
  if (!flavorName) {
    const c = customizations.find(
      (x) => String(x.key).toLowerCase() === "flavor"
    );
    if (c) flavorName = c.value;
  }

  return flavorName;
};

const pushIfMissing = (list, keyLower, entry) => {
  const exists = list.some(
    (c) => String(c.key).toLowerCase() === keyLower && !!c.value
  );
  if (!exists && entry?.value) list.push(entry);
};

const valueFromSize = (size, cVal) => {
  // if you have a tiered size, make it pretty; else use provided value
  if (size?.tierCount && size?.name) {
    return getSizeLabel({ displayName: size.name, tierCount: size.tierCount });
  }
  return cVal ?? size?.displayName ?? size?.name ?? size;
};

const valueFromColor = (color, cVal) =>
  cVal ?? color?.name ?? color?.value ?? color;

const isHexColor = (s) =>
  typeof s === "string" && /^#[0-9a-f]{6}$/i.test(s || "");

const formatCartItemDetails = (item = {}) => {
  const lines = [];
  const customizations = toArrayCustomizations(item);

  // Name (use displayName; if you prefer item.name, swap below)
  const displayName = item.displayName || "";
  const flavorName = pickFlavorName(item, customizations);

  const normalizedDisplayName = normalizeString(displayName);
  const normalizedFlavorName = normalizeString(flavorName);

  const isNameDifferent =
    normalizedDisplayName && normalizedFlavorName
      ? normalizedDisplayName !== normalizedFlavorName
      : !!displayName;

  if (displayName && isNameDifferent) {
    lines.push({ label: "Name", value: displayName });
  }

  // Flavor
  if (flavorName) {
    // keep original casing from customization if present
    const flavorPretty =
      customizations.find((c) => String(c.key).toLowerCase() === "flavor")
        ?.value || flavorName;
    lines.push({ label: "Flavor", value: flavorPretty });
  }

  // Merge-in size/color if missing (from top-level fields)
  pushIfMissing(customizations, "size", {
    key: "size",
    label: "Size",
    value: valueFromSize(item.size, undefined),
  });

  pushIfMissing(customizations, "color", {
    key: "color",
    label: "Color",
    value: valueFromColor(item.color, undefined),
  });

  // Remaining customizations (skip flavor)
  customizations.forEach((c) => {
    const keyLower = String(c.key || "").toLowerCase();
    if (keyLower === "flavor") return;

    let val = c.value;

    if (keyLower === "size") {
      val = valueFromSize(item.size, c.value);
    }
    if (keyLower === "color") {
      val = valueFromColor(item.color, c.value);
    }

    lines.push({
      key: c.key,
      label: c.label || formatKeyLabel(c.key),
      value: val,
      isColor: isHexColor(val),
    });
  });

  // filter out empty values
  return lines.filter((l) => l.value != null && l.value !== "");
};

export default formatCartItemDetails;
